import './styles.css';
import { Root, Content } from '@radix-ui/react-collapsible';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Input from 'src/components/Input/Input';
import { WorkflowActionFieldName } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowPopupBock/WorkflowEscalatedList/WorkflowActionFields/WorkflowActionFieldName';
import { WorkflowActionFieldEmail } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowPopupBock/WorkflowEscalatedList/WorkflowActionFields/WorkflowActionFieldEmail';
import { WorkflowActionFieldPhone } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowPopupBock/WorkflowEscalatedList/WorkflowActionFields/WorkflowActionFieldPhone';
import { WorkflowActionFieldCustom } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowPopupBock/WorkflowEscalatedList/WorkflowActionFields/WorkflowActionFieldCustom';
import { WorkflowActionFieldCustomArray } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowPopupBock/WorkflowEscalatedList/WorkflowActionFields/WorkflowActionFieldCustomArray';
import { clsx } from 'clsx';

export const EscalatedListItem = ({
  label,
  description,
  value,
  isExpanded,
  listItemContentClassName = '',
}) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(isExpanded);
  }, [isExpanded]);

  const {
    control,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext();

  const isError = Array.isArray(errors?.action) ? errors?.action[0]?.action_data_collection : false;

  return (
    <Root className="group transition rounded-sm-5" open={open} onOpenChange={setOpen}>
      <div className="flex items-center space-x-[7px] space-y-0 w-full h-full">
        <label
          htmlFor={`radio-item-${value}`}
          className="py-2.5 cursor-pointer w-full h-full flex flex-row gap-3 items-start"
        >
          <RadioGroupItem
            {...({ name: 'action.0.action_escalated_list' } as any)}
            className={'mt-[5px]'}
            value={value}
            id={`radio-item-${value}`}
          />
          <div className={'flex flex-col w-full'}>
            <p className="font-semibold text-base text-ebony_opacity_80 group-hover:text-ebony transition-colors">
              {label}
            </p>
            <p className={'text-sm font-medium text-ebony_opacity_50'}>{description}</p>
          </div>
        </label>
      </div>
      {['collect_and_escalate', 'forward_to_email'].includes(value) && (
        <Content
          className={clsx(
            'CollapsibleContent bg-light border border-[#B8C1C633] rounded-sm-5 shadow-[0_2px_2px_0_rgba(0,0,0,0.25)]',
            listItemContentClassName,
          )}
        >
          <div className={'p-5'}>
            <div className={'flex flex-col w-full h-full mb-2.5 gap-5'}>
              {value === 'forward_to_email' && (
                <div className={'flex flex-col w-full gap-[5px] px-2'}>
                  <Controller
                    control={control}
                    name="action.0.receiver_email_addresses"
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Input
                        type={'text'}
                        label={'Forward to'}
                        onChange={onChange}
                        value={value}
                        isValid={!error?.message}
                        invalidMessage={error?.message}
                        className={'!py-2 !px-3.5'}
                        labelClassName={'text-sm text-ebony_opacity_80 font-semibold'}
                        placeholder={'email1@example.com, email2@example.com, email3@example.com'}
                      />
                    )}
                  />
                </div>
              )}
              <div className={'flex flex-col w-full gap-[5px] px-2'}>
                <Controller
                  control={control}
                  name="action.0.message_before"
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Input
                      type={'text'}
                      label={'AI message before data collection'}
                      placeholder={'To get you the right support faster, I’ll need to gather...'}
                      invalidMessage={error?.message}
                      value={value}
                      isValid={!error?.message}
                      onChange={onChange}
                      className={'!py-2 !px-3.5'}
                      labelClassName={'text-sm text-ebony_opacity_80 font-semibold'}
                    />
                  )}
                />
              </div>
              <div className={'flex flex-col w-full gap-2.5 px-2'}>
                <div>
                  <p
                    className={clsx(
                      'text-sm text-ebony_opacity_80 font-semibold',
                      isError && 'text-error',
                    )}
                  >
                    Data collection
                  </p>
                </div>
                <div className={'flex flex-row gap-5 justify-around w-full'}>
                  <WorkflowActionFieldName control={control} trigger={trigger} />
                  <WorkflowActionFieldEmail control={control} trigger={trigger} />
                  <WorkflowActionFieldPhone control={control} trigger={trigger} />
                  <WorkflowActionFieldCustom control={control} trigger={trigger} />
                </div>
              </div>
              <WorkflowActionFieldCustomArray control={control} watch={watch} />
              <div className={'flex flex-col w-full gap-[5px] px-2'}>
                <Controller
                  control={control}
                  name="action.0.message_after"
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Input
                      type={'text'}
                      label={'AI message after data collection'}
                      placeholder={'Thank you for sharing your details. I’m now connecting...'}
                      invalidMessage={error?.message}
                      value={value}
                      isValid={!error?.message}
                      onChange={onChange}
                      className={'!py-2 !px-3.5'}
                      labelClassName={'text-sm text-ebony_opacity_80 font-semibold'}
                    />
                  )}
                />
              </div>
              {value === 'forward_to_email' && (
                <div className={'flex flex-col w-full gap-2.5 px-2'}>
                  <div>
                    <p className={'text-sm text-ebony_opacity_80 font-semibold'}>
                      Send conversation summary or entire transcript?
                    </p>
                  </div>
                  <Controller
                    control={control}
                    name="action.0.action_message_type"
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <RadioGroup
                        onValueChange={onChange}
                        value={value || 'transcript'}
                        defaultValue={'transcript'}
                        className="flex flex-row gap-5"
                      >
                        <label
                          htmlFor={`radio-item-transcript`}
                          className="cursor-pointer flex flex-row gap-3 items-center"
                        >
                          <RadioGroupItem value={'transcript'} id={`radio-item-transcript`} />
                          <p className="font-medium text-sm text-ebony_opacity_50 opacity-50 group-hover:opacity-100 group-[&[data-state=open]]:opacity-100 transition">
                            Entire transcript
                          </p>
                        </label>
                        <label
                          htmlFor={`radio-item-summary`}
                          className="cursor-pointer flex flex-row gap-3 items-center"
                        >
                          <RadioGroupItem value={'summary'} id={`radio-item-summary`} />
                          <p className="font-medium text-sm text-ebony_opacity_50 opacity-50 group-hover:opacity-100 group-[&[data-state=open]]:opacity-100 transition">
                            Summary
                          </p>
                        </label>
                      </RadioGroup>
                    )}
                  />
                </div>
              )}
            </div>
          </div>
        </Content>
      )}
      {'close' === value && (
        <Content
          className={clsx(
            'CollapsibleContent bg-light border border-[#B8C1C633] rounded-sm-5 shadow-[0_2px_2px_0_rgba(0,0,0,0.25)]',
            listItemContentClassName,
          )}
        >
          <div className={'flex flex-col w-full h-full mb-2.5 gap-5 p-5'}>
            <div className={'flex flex-col w-full gap-[5px] px-2'}>
              <Controller
                control={control}
                name="action.0.message_close"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    type={'text'}
                    label={'Closing message'}
                    placeholder={'To get help, submit the ticket here OR email us at…'}
                    invalidMessage={error?.message}
                    value={value}
                    isValid={!error?.message}
                    onChange={onChange}
                    className={'!py-2 !px-3.5'}
                    labelClassName={'text-sm text-ebony_opacity_80 font-semibold'}
                  />
                )}
              />
            </div>
          </div>
        </Content>
      )}
    </Root>
  );
};
